import { ChevronLeft, Loader2 as Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';

interface ValidationProgress {
  validated: number;
  total: number;
  errors: number;
}

interface StatusBarProps {
  onSaveAndClose: () => void;
  isSaving: boolean;
  progress: ValidationProgress;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
}

export function StatusBar({
  onSaveAndClose,
  isSaving,
  progress,
  onSubmit,
  isSubmitting,
  canSubmit,
}: StatusBarProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-between px-4 py-3 bg-terre-800">
      <Button
        variant="ghost"
        onClick={onSaveAndClose}
        disabled={isSaving}
        aria-label={t('actions.saveAndClose')}
        aria-busy={isSaving}
        className="ml-4 gap-2.5 bg-terra-cotta-100 text-terra-cotta-600 border-0 px-4 py-2 hover:bg-terra-cotta-100/80"
      >
        {isSaving ? (
          <Loader className="size-4 animate-spin text-terra-cotta-600" />
        ) : (
          <ChevronLeft className="size-4 text-terra-cotta-600" />
        )}
        {t('actions.saveAndClose')}
      </Button>

      {progress && (
        <span className="absolute left-1/2 -translate-x-1/2 text-terra-cotta-100 text-sm font-medium">
          {t('statusBar.progress', {
            validated: progress.validated,
            total: progress.total,
            errors: progress.errors,
          })}
        </span>
      )}

      <Tooltip open={canSubmit ? false : undefined}>
        <TooltipTrigger asChild>
          <span className="mr-4">
            <Button
              onClick={onSubmit}
              disabled={!canSubmit || isSubmitting}
              aria-label={t('actions.submitValidation')}
              aria-busy={isSubmitting}
              className="gap-2.5 bg-[#B15334] text-white border-0 px-4 py-2 hover:bg-[#9A472D] disabled:bg-[#D48F79] disabled:opacity-100 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? t('actions.submittingValidation')
                : t('actions.submitValidation')}
              {isSubmitting && (
                <Loader className="size-4 animate-spin text-white" />
              )}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{t('statusBar.submitDisabledTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
